import {
    type OrganizationUserParticipantFragment,
    OrganizationUserRole,
    type ProjectUserRole,
    type ProjectUser,
} from '@gql/graphql';
import { type SimpleUser } from '@models/SimpleUser';
import { type ProjectParticipant } from './useProjectParticipants';

interface CommonUserProps {
    id: string;
    lastName: string;
    owner?: boolean;
    icso?: boolean;
    role?: { id: string };
    organizationRole?: OrganizationUserRole;
    member?: boolean;
}

// TODO: use api to get roles
export const DEFAULT_ROLE_ID = 'PR01GB7SMDRETD2N507CMZBZR1QK';
export const OWN_ROLE_ID = 'PR01GB83N5ARY5TZ5XDXWZG0PFV8';
export const READ_ONLY_ROLE = 'PR01K49PCC26NAJ5AJWPEBWHYNJ0';

export type ProjectMember = SimpleUser &
    ProjectUserStatus & {
        participant: ProjectParticipant;
    };

export interface ProjectUserStatus {
    role?: ProjectUserRole;
    icso: boolean;
    owner: boolean;
    readonly: boolean;
}

export class ProjectRolesHelper {
    static Roles = [
        {
            id: OWN_ROLE_ID,
            name: 'Project owner',
        },
        {
            id: DEFAULT_ROLE_ID,
            name: 'Project member',
        },
        {
            id: READ_ONLY_ROLE,
            name: 'Project member (read only)',
        },
    ];
    readonly members: string[];
    readonly readonlyMembers: string[];
    readonly ICSOs: string[];
    readonly owners: string[];

    constructor(participants: ProjectParticipant[]) {
        this.readonlyMembers = participants
            .filter((participant): participant is ProjectUser => {
                return (
                    participant.__typename === 'ProjectUser' &&
                    participant.role?.id === READ_ONLY_ROLE &&
                    Boolean(participant.user)
                );
            })
            .map(({ user: { id } }) => id);

        this.members = participants
            .filter((participant): participant is ProjectUser => {
                return (
                    participant.__typename === 'ProjectUser' &&
                    participant.role?.id === DEFAULT_ROLE_ID &&
                    Boolean(participant.user)
                );
            })
            .map(({ user: { id } }) => id);
        this.ICSOs = participants
            .filter(
                (
                    participant,
                ): participant is OrganizationUserParticipantFragment => {
                    return (
                        participant !== null &&
                        participant.__typename === 'OrganizationUser' &&
                        participant.organizationRole ===
                            OrganizationUserRole.Icso &&
                        Boolean(participant.user)
                    );
                },
            )
            .map(({ user: { id } }) => id);

        this.owners = participants
            .filter((participant): participant is ProjectUser => {
                return (
                    participant.__typename === 'ProjectUser' &&
                    participant.role?.id === OWN_ROLE_ID &&
                    Boolean(participant.user)
                );
            })
            .map(({ user: { id } }) => id);
    }

    sortByRole(a: CommonUserProps, b: CommonUserProps): number {
        return (
            this.rolePriority(a) - this.rolePriority(b) ||
            a.lastName.localeCompare(b.lastName)
        );
    }

    getRole(userId: string): ProjectUserRole | undefined {
        if (this.isOwner(userId)) {
            return ProjectRolesHelper.Roles.find(
                ({ id }) => id === OWN_ROLE_ID,
            );
        }
        if (this.members.includes(userId)) {
            return ProjectRolesHelper.Roles.find(
                ({ id }) => id === DEFAULT_ROLE_ID,
            );
        }
        if (this.readonlyMembers.includes(userId)) {
            return ProjectRolesHelper.Roles.find(
                ({ id }) => id === READ_ONLY_ROLE,
            );
        }
        return undefined;
    }

    isIcso(userId: string): boolean {
        return this.ICSOs.includes(userId);
    }

    isIcsoOnly(userId: string): boolean {
        return this.isIcso(userId) && !this.isMember(userId);
    }

    isMember(userId: string): boolean {
        return (
            this.readonlyMembers.includes(userId) ||
            this.members.includes(userId) ||
            this.isOwner(userId)
        );
    }
    isReadOnly(userId: string): boolean {
        return this.readonlyMembers.includes(userId);
    }

    isOwner(userId: string): boolean {
        return this.owners.includes(userId);
    }

    mapParticipantToProjectMember(
        participant: ProjectParticipant,
    ): ProjectMember {
        return {
            id: participant.user.id,
            firstName: participant.user.firstName,
            lastName: participant.user.lastName,
            ...this.getProjectUserStatus(participant.user.id),
            participant,
        };
    }

    getProjectUserStatus(userId: string): ProjectUserStatus {
        return {
            role: this.getRole(userId),
            icso: this.isIcso(userId),
            owner: this.isOwner(userId),
            readonly: this.isReadOnly(userId),
        };
    }

    private rolePriority(user: CommonUserProps): number {
        if (user.role?.id === OWN_ROLE_ID) {
            return 1;
        }
        if (user.role?.id === DEFAULT_ROLE_ID) {
            return 2;
        }
        if (user.role?.id === READ_ONLY_ROLE) {
            return 3;
        }
        if (this.isIcsoOnly(user.id)) {
            return 4;
        }
        return 5;
    }
}
